import {
  Flex,
  Text,
  Box,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Button,
  Menu,
  MenuButton,
  MenuGroup,
  MenuList,
  TabsProps as ChakraTabsProps,
  Tooltip,
  Image
} from '@chakra-ui/react';
import {
  faUserPlus,
  faChevronDown,
  faBars,
  faCheckCircle,
  faSliders,
  faInfoCircle,
  faSearch
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button as MRButton,
  ButtonText,
  Tabs,
  MenuItem,
  ButtonIcon,
  SearchInput
} from '@morressier/alembic';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  fetchWorkspaceInActiveJournals,
  fetchWorkspaceJournals
} from 'src/api/queries';
import {
  getOrgId,
  useWorkspace
} from '../../SubmissionContexts/Users/userHook';
import Footer from '../../../../../components/Footer';
import ScrollToTopButton from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/ScrollToTopButton';
import CreateJournalForm from './CreateJournalForm';
import SubmissionLoader from '../../Submissions/SubmissionLoader';
import { ISubmissionContextJournal } from '@publishing-engine/domain/views';
import ActivateDeactivateJournalModal from 'src/components/ActivateDeactivateJournalModal';
import { renderBadgeTypes } from '../../SubmissionContexts';
import { formatDate } from 'src/utils/date';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';

interface JournalTableProps {
  journals: ISubmissionContextJournal[];
  isInactive?: boolean;
  searchParams?: string;
  onToggleStatus?: (journal: ISubmissionContextJournal) => void;
  backPage?: number;
}

const JournalTable = ({
  journals,
  isInactive,
  searchParams,
  onToggleStatus,
  backPage
}: JournalTableProps) => {
  const navigate = useNavigate();

  if (journals.length === 0) {
    const hasSearch = Boolean(searchParams?.trim());

    return (
      <SubmissionLoader
        title={
          hasSearch
            ? 'No journals found'
            : isInactive
              ? 'No inactive journals'
              : 'No journals'
        }
        subTitle={
          hasSearch
            ? 'No journals found matching your search criteria. Try adjusting your search terms.'
            : isInactive
              ? 'All journals are currently active. There are no inactive journals to display.'
              : 'No journals have been created yet. Click "Create Journal" to set up your first journal.'
        }
        bgColor="neutralSecondaryFill"
      />
    );
  }

  return (
    <Box width="100%" mt={4} overflowX="auto">
      <Table minWidth="320px">
        <Thead>
          <Tr
            data-testId="journal-table-header"
            gap={4}
            sx={{
              '& th': {
                textTransform: 'none',
                color: 'black'
              }
            }}
            background="white"
          >
            <Th>
              <Text textStyle="headingItem" whiteSpace="nowrap">
                Journal
              </Text>
            </Th>
            <Th>
              <Text textStyle="headingItem">Supported Article Types</Text>
            </Th>
            <Th display="none">
              <Flex gap={2} alignItems="center">
                <Text textStyle="headingItem">Transfers To</Text>
                <Tooltip
                  hasArrow
                  placement="top"
                  borderRadius={4}
                  shouldWrapChildren
                  label="Journals that can receive submissions through a “Reject and Transfer” editorial decision"
                >
                  <ButtonIcon
                    size="small"
                    ariaLabel="Help Icon"
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                  />
                </Tooltip>
              </Flex>
            </Th>
            <Th>
              <Text textStyle="headingItem">Key Dates</Text>
            </Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {journals.map(journal => (
            <Tr
              key={journal.id}
              data-testId="journal-table-row"
              gap={4}
              background="white"
            >
              <Td
                onClick={() => {
                  navigate(`${journal.id}/edit`, {
                    state: {
                      backTab: isInactive ? 'inactive' : 'active',
                      backPage,
                      canActivate: journal.canActivate ?? false,
                      canDeactivate: journal.canDeactivate ?? false,
                      submissionCount: journal.submissionCount ?? 0
                    }
                  });
                }}
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                maxWidth={'500px'}
              >
                <Flex flexDirection="row" gap={2}>
                  <Flex
                    width="125px"
                    height="50px"
                    borderRadius="md"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Image
                      src={journal.config?.logoUrl}
                      alt={`${journal.data?.name ?? 'Journal'} logo`}
                      width="100%"
                      height="100%"
                      objectFit="contain"
                      fallback={
                        <Text textStyle="headingLine" color="grey">
                          {'No Logo'}
                        </Text>
                      }
                    />
                  </Flex>
                  <Tooltip
                    hasArrow
                    borderRadius={4}
                    placement="top"
                    label={'Edit journal'}
                  >
                    <Flex
                      direction="column"
                      alignItems="flex-start"
                      justifyContent="flex-start"
                      width="100%"
                      minWidth={0}
                      textAlign="left"
                    >
                      <Box width="100%" overflow="hidden">
                        <Text
                          wordBreak="break-word"
                          textStyle="headingLine"
                          color="actionPrimaryFill"
                          textDecoration="none"
                          _hover={{
                            textDecoration: 'underline',
                            textDecorationColor: 'actionPrimaryFill'
                          }}
                        >
                          {journal.data.name}
                        </Text>
                      </Box>
                      <Flex alignItems="flex-start" flexDirection="column">
                        <Text textTransform="uppercase" fontWeight="bold">
                          {journal.data.shortname}
                        </Text>
                        <Text color="grey">
                          {journal.submissionCount ?? 0} Submission
                          {(journal.submissionCount ?? 0) > 1 ? 's' : ''}
                        </Text>
                      </Flex>
                    </Flex>
                  </Tooltip>
                </Flex>
              </Td>
              <Td maxWidth="220px">
                <Text>{renderBadgeTypes(journal.articleTypes ?? [])}</Text>
              </Td>
              <Td maxWidth="220px" display="none">
                <Text>{renderBadgeTypes(journal.transferJournals ?? [])}</Text>
              </Td>
              <Td textStyle="textContext">
                <Flex flexDirection="column" gap={1}>
                  {isInactive && (
                    <Text>
                      <strong>Created:</strong>{' '}
                      {journal.createdAt
                        ? formatDate(new Date(journal.createdAt))
                        : 'N/A'}
                    </Text>
                  )}
                  {!isInactive && (
                    <Text>
                      <strong>Activated:</strong>{' '}
                      {journal.config.activationDate
                        ? formatDate(new Date(journal.config.activationDate))
                        : journal.createdAt
                        ? formatDate(new Date(journal.createdAt))
                        : 'N/A'}
                    </Text>
                  )}
                  <Text>
                    <strong>Last Updated:</strong>{' '}
                    {journal.modifiedAt
                      ? formatDate(new Date(journal.modifiedAt))
                      : 'N/A'}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex justifyContent="flex-end" width="100%">
                  <Menu aria-label="journal actions menu">
                    <MenuButton>
                      <ButtonText>
                        <Flex gap={2} alignItems="center">
                          <FontAwesomeIcon size="lg" icon={faBars} />
                        </Flex>
                      </ButtonText>
                    </MenuButton>

                    <MenuList>
                      {/* Activate for inactive journals, Deactivate for active journals */}
                      {onToggleStatus &&
                        (() => {
                          const canActivate = isInactive && journal.canActivate;
                          const canDeactivate =
                            !isInactive && journal.canDeactivate;
                          const isDisabled = !canActivate && !canDeactivate;
                          const tooltipLabel = isInactive
                            ? 'Journal role assignment should be configured before you can activate this journal.'
                            : 'This journal has active submissions and cannot be deactivated.';

                          if (isDisabled) {
                            return (
                              <Tooltip
                                hasArrow
                                borderRadius={4}
                                placement="top"
                                label={tooltipLabel}
                              >
                                <Box>
                                  <MenuItem
                                    ariaLabel={
                                      isInactive
                                        ? 'Activate Journal'
                                        : 'Deactivate Journal'
                                    }
                                    isDisabled
                                    onClick={() => {}}
                                    disabled={isDisabled}
                                  >
                                    <Flex gap={2} alignItems="center">
                                      <FontAwesomeIcon icon={faCheckCircle} />
                                      <Text textStyle="context">
                                        {isInactive ? 'Activate' : 'Deactivate'}
                                      </Text>
                                    </Flex>
                                  </MenuItem>
                                </Box>
                              </Tooltip>
                            );
                          }

                          return (
                            <MenuItem
                              ariaLabel={
                                isInactive
                                  ? 'Activate Journal'
                                  : 'Deactivate Journal'
                              }
                              onClick={() => onToggleStatus(journal)}
                            >
                              <Flex gap={2} alignItems="center">
                                <FontAwesomeIcon icon={faCheckCircle} />
                                <Text textStyle="context">
                                  {isInactive ? 'Activate' : 'Deactivate'}
                                </Text>
                              </Flex>
                            </MenuItem>
                          );
                        })()}
                      <MenuItem
                        ariaLabel="Edit Journal"
                        onClick={() => {
                          navigate(`${journal.id}/edit`, {
                            state: {
                              backTab: isInactive ? 'inactive' : 'active',
                              backPage,
                              canActivate: journal.canActivate ?? false,
                              canDeactivate: journal.canDeactivate ?? false,
                              submissionCount: journal.submissionCount ?? 0
                            }
                          });
                        }}
                      >
                        <Flex gap={2} alignItems="center">
                          <FontAwesomeIcon icon={faSliders} />
                          <Text textStyle="context">Edit</Text>
                        </Flex>
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const ManageJournals = () => {
  const organizationId = getOrgId();
  const { workspaceId } = useWorkspace();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose
  } = useDisclosure();
  const location = useLocation();
  const initialState = useMemo(() => {
    const state = (location.state as Record<string, unknown> | null) ?? {};
    const backTab =
      typeof state.backTab === 'string' ? state.backTab : 'active';
    const backPage = typeof state.backPage === 'number' ? state.backPage : 1;
    return {
      tabIndex: backTab === 'inactive' ? 1 : 0,
      activePage: backTab === 'active' ? backPage : 1,
      inactivePage: backTab === 'inactive' ? backPage : 1
    };
  }, [location.state]);
  const [activeTabIndex, setActiveTabIndex] = useState(initialState.tabIndex);
  const [activePage, setActivePage] = useState(initialState.activePage);
  const [inactivePage, setInactivePage] = useState(initialState.inactivePage);
  const [activePageSize, setActivePageSize] =
    useState<number>(DEFAULT_PAGE_SIZE);
  const [inactivePageSize, setInactivePageSize] =
    useState<number>(DEFAULT_PAGE_SIZE);
  const [selectedJournal, setSelectedJournal] =
    useState<ISubmissionContextJournal | null>(null);
  const [activeSearchValue, setActiveSearchValue] = useState('');
  const [activeSearchParams, setActiveSearchParams] = useState('');
  const [inactiveSearchValue, setInactiveSearchValue] = useState('');
  const [inactiveSearchParams, setInactiveSearchParams] = useState('');
  const queryClient = useQueryClient();

  const activeOffset = useMemo(
    () => (activePage - 1) * activePageSize,
    [activePage, activePageSize]
  );
  const inactiveOffset = useMemo(
    () => (inactivePage - 1) * inactivePageSize,
    [inactivePage, inactivePageSize]
  );

  const { data: activeData } = useQuery({
    queryKey: [
      'workspace-journals',
      {
        workspaceId,
        activePage,
        activePageSize,
        searchParams: activeSearchParams
      }
    ],
    queryFn: () =>
      workspaceId
        ? fetchWorkspaceJournals(
            workspaceId,
            activeOffset,
            activePageSize,
            activeSearchParams
          )
        : null
  });

  const { data: inactiveData } = useQuery({
    queryKey: [
      'workspace-journals',
      {
        workspaceId,
        isActive: false,
        inactivePage,
        inactivePageSize,
        searchParams: inactiveSearchParams
      }
    ],
    queryFn: () =>
      workspaceId
        ? fetchWorkspaceInActiveJournals(
            workspaceId,
            inactiveOffset,
            inactivePageSize,
            false,
            inactiveSearchParams
          )
        : null
  });

  const handleJournalCreated = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['workspace-journals']
    });
    setActivePage(1);
    setInactivePage(1);
  }, [queryClient]);

  const handleToggleStatusSuccess = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['workspace-journals']
    });
    setActivePage(1);
    setInactivePage(1);
  }, [queryClient]);

  const handleToggleStatus = (journal: ISubmissionContextJournal) => {
    setSelectedJournal(journal);
    onConfirmOpen();
  };

  const handleSearch = useCallback(
    (value: string, tab: 'active' | 'inactive') => {
      if (tab === 'active') {
        setActiveSearchParams(value);
        setActivePage(1);
      } else {
        setInactiveSearchParams(value);
        setInactivePage(1);
      }
    },
    []
  );

  const handleSearchKeyDown = useCallback(
    (tab: 'active' | 'inactive') =>
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const value =
            tab === 'active' ? activeSearchValue : inactiveSearchValue;
          handleSearch(value.trim(), tab);
        }
      },
    [handleSearch, activeSearchValue, inactiveSearchValue]
  );

  const handleSearchClear = useCallback(
    (tab: 'active' | 'inactive') => {
      if (tab === 'active') {
        setActiveSearchValue('');
      } else {
        setInactiveSearchValue('');
      }
      handleSearch('', tab);
    },
    [handleSearch]
  );

  const activeSearchInput = useMemo(
    () => (
      <Flex width={{ base: '100%' }} marginBottom={4}>
        <SearchInput
          placeholder="Search journal by title/short name"
          iconLeft={<FontAwesomeIcon icon={faSearch} />}
          width="100%"
          name="activeSearchParams"
          value={activeSearchValue}
          enterKeyHint="search"
          onChange={e => setActiveSearchValue(e.currentTarget.value)}
          onInputClear={() => handleSearchClear('active')}
          onKeyDown={handleSearchKeyDown('active')}
        />
      </Flex>
    ),
    [activeSearchValue, handleSearchKeyDown, handleSearchClear]
  );

  const inactiveSearchInput = useMemo(
    () => (
      <Flex width={{ base: '100%' }} marginBottom={4}>
        <SearchInput
          placeholder="Search journal by title/short name"
          iconLeft={<FontAwesomeIcon icon={faSearch} />}
          width="100%"
          name="inactiveSearchParams"
          value={inactiveSearchValue}
          enterKeyHint="search"
          onChange={e => setInactiveSearchValue(e.currentTarget.value)}
          onInputClear={() => handleSearchClear('inactive')}
          onKeyDown={handleSearchKeyDown('inactive')}
        />
      </Flex>
    ),
    [inactiveSearchValue, handleSearchKeyDown, handleSearchClear]
  );

  const activeJournals = useMemo(
    () => activeData?.collection || [],
    [activeData?.collection]
  );

  const inactiveJournals = useMemo(
    () => inactiveData?.collection || [],
    [inactiveData?.collection]
  );

  const handleTabChange = useCallback((index: number) => {
    setActiveTabIndex(index);
    setActiveSearchValue('');
    setActiveSearchParams('');
    setInactiveSearchValue('');
    setInactiveSearchParams('');
  }, []);

  const chakraTabsProps = useMemo(
    () =>
      ({
        index: activeTabIndex,
        onChange: handleTabChange
      } as ChakraTabsProps),
    [activeTabIndex, handleTabChange]
  );

  const tabs = useMemo(
    () => [
      {
        label: `Active Journals (${activeData?.total ?? 0})`,
        content: (
          <>
            {activeSearchInput}
            <JournalTable
              journals={activeJournals}
              onToggleStatus={handleToggleStatus}
              isInactive={false}
              searchParams={activeSearchParams}
              backPage={activePage}
            />
            <TablePagination
              ariaLabel="Active journals pagination"
              currentPage={activePage}
              totalItems={activeData?.total ?? 0}
              pageSize={activePageSize}
              onPageChange={setActivePage}
              onPageSizeChange={setActivePageSize}
            />
          </>
        )
      },
      {
        label: `Inactive Journals (${inactiveData?.total ?? 0})`,
        content: (
          <>
            {inactiveSearchInput}
            <JournalTable
              journals={inactiveJournals}
              isInactive={true}
              searchParams={inactiveSearchParams}
              onToggleStatus={handleToggleStatus}
              backPage={inactivePage}
            />
            <TablePagination
              ariaLabel="Inactive journals pagination"
              currentPage={inactivePage}
              totalItems={inactiveData?.total ?? 0}
              pageSize={inactivePageSize}
              onPageChange={setInactivePage}
              onPageSizeChange={setInactivePageSize}
            />
          </>
        )
      }
    ],
    [
      activeJournals,
      inactiveJournals,
      handleToggleStatus,
      activeData?.total,
      inactiveData?.total,
      activeSearchParams,
      inactiveSearchParams,
      activePage,
      inactivePage,
      activePageSize,
      inactivePageSize,
      setActivePage,
      setInactivePage,
      setActivePageSize,
      setInactivePageSize,
      activeSearchInput,
      inactiveSearchInput
    ]
  );

  return (
    <Box id="scrollable-wrapper" overflowY="auto" height="100%">
      <Box padding={5} minHeight="100%">
        <Flex
          alignItems="flex-start"
          gap={4}
          width="100%"
          justifyContent="space-between"
          marginBottom={4}
        >
          <Text textStyle="headingSection">Journals</Text>
          <Box marginStart="auto">
            <MRButton
              iconLeft={<FontAwesomeIcon icon={faUserPlus} />}
              onClick={onOpen}
            >
              Create Journal
            </MRButton>
          </Box>
        </Flex>

        <Box display={{ base: 'block', md: 'none' }} marginBottom={4}>
          <Menu aria-label="journal tabs menu">
            <MenuButton
              borderWidth="0"
              borderRadius="0"
              variant="outline"
              as={Button}
              width="100%"
              justifyContent="space-between"
              textAlign="left"
              bg="white"
              border="1px solid"
              borderColor="neutralPrimaryBorder"
              rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
              _hover={{ bg: 'white' }}
              _active={{ bg: 'white' }}
            >
              {tabs[activeTabIndex]?.label}
            </MenuButton>
            <MenuList width="100%">
              <MenuGroup title="Manage Journals">
                {tabs.map((tab, index) => (
                  <MenuItem
                    ariaLabel={tab.label}
                    key={tab.label}
                    onClick={() => handleTabChange(index)}
                    color={
                      activeTabIndex === index ? 'actionPrimaryFill' : undefined
                    }
                    fontWeight={
                      activeTabIndex === index ? 'semibold' : undefined
                    }
                  >
                    {tab.label}
                  </MenuItem>
                ))}
              </MenuGroup>
            </MenuList>
          </Menu>
        </Box>

        <Box display={{ base: 'none', md: 'block' }}>
          <Tabs
            chakraTabsProps={chakraTabsProps}
            tabs={tabs.map(tab => ({ label: tab.label, content: null }))}
          />
        </Box>

        {tabs[activeTabIndex]?.content}
      </Box>
      <Footer />
      <ScrollToTopButton />

      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Journal</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {workspaceId && organizationId && (
              <CreateJournalForm
                workspaceId={workspaceId}
                organizationId={organizationId}
                onSuccess={handleJournalCreated}
                onClose={onClose}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <ActivateDeactivateJournalModal
        journal={selectedJournal}
        isOpen={isConfirmOpen}
        onClose={() => {
          onConfirmClose();
          setSelectedJournal(null);
        }}
        onSuccess={handleToggleStatusSuccess}
        action={
          selectedJournal?.canActivate === true ? 'activate' : 'deactivate'
        }
      />
    </Box>
  );
};

export default ManageJournals;
